import * as React from 'react';
import {AccountAvatar} from 'src/AccountAvatar';
import {ACCOUNT_SECTIONS} from 'src/AccountsView/accountFilter';
import {FilterDropdown} from 'src/components/FilterDropdown';
import type {AccountListItemFragment} from '@transactions/graphql';

type Props = {
  accounts: AccountListItemFragment[];
  /** Selected account ids; empty means all accounts (no filter). */
  value: string[];
  onChange: (value: string[]) => void;
};

/**
 * Multi-select account filter pill (FilterDropdown in `multiple` mode) for the
 * cross-account Activity list. The trigger reads "All accounts" when nothing is
 * picked, the single account's name when one is, and "N accounts" otherwise.
 *
 * The menu groups accounts by type (the overview's sections: Checking,
 * Savings, Credit Card, …, Investments), alphabetized within each group, and
 * carries a type-ahead box — with dozens of accounts, typing beats scrolling.
 * Group headers toggle their whole type at once (FilterDropdown behavior).
 */
export const AccountFilterSelect = ({accounts, value, onChange}: Props) => {
  const selectedCount = value.length;
  const triggerLabel =
    selectedCount === 0
      ? 'All accounts'
      : selectedCount === 1
        ? (accounts.find((account) => account._id === value[0])?.name ??
          '1 account')
        : `${selectedCount} accounts`;

  const options = React.useMemo(
    () =>
      ACCOUNT_SECTIONS.flatMap((section) =>
        accounts
          .filter((account) => section.matches(account))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((account) => ({
            key: account._id,
            label: account.name,
            group: section.label,
            icon: <AccountAvatar account={account} />,
          })),
      ),
    [accounts],
  );

  return (
    <FilterDropdown
      multiple
      searchable
      label="Accounts"
      allLabel="All accounts"
      triggerLabel={triggerLabel}
      options={options}
      value={value}
      onChange={onChange}
    />
  );
};
